import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MapPin, Clock, Store, Package, Phone } from 'lucide-react';
import { useLocation } from '../contexts/LocationContext';
import Header from '../components/Header';
import ProductCard from '../components/ProductCard';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MerchantDetailPage() {
    const { alias } = useParams();
    const navigate = useNavigate();
    const { location } = useLocation();

    const [merchant, setMerchant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchMerchantDetail = async () => {
            try {
                setLoading(true);
                setError(null);

                // Use dedicated backend endpoint to find merchant by alias
                const response = await axios.get(`${API}/merchants/${alias}`, {
                    params: {
                        lon: location.lon,
                        lat: location.lat
                    }
                });

                if (response.data.status === 'success' && response.data.data) {
                    setMerchant(response.data.data);
                } else {
                    setError('Toko tidak ditemukan');
                }
            } catch (err) {
                console.error('Error fetching merchant:', err);
                if (err.response?.status === 404) {
                    setError('Toko tidak ditemukan');
                } else {
                    setError('Terjadi kesalahan saat memuat data toko');
                }
                toast.error('Gagal memuat data toko');
            } finally {
                setLoading(false);
            }
        };

        fetchMerchantDetail();
    }, [alias, location]);

    const handleBack = () => {
        navigate('/');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F8F9FA]">
                <Header />
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
                        <div className="h-48 bg-gray-200 rounded-2xl mb-6"></div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[...Array(8)].map((_, i) => (
                                <div key={i} className="h-64 bg-gray-200 rounded-xl"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !merchant) {
        return (
            <div className="min-h-screen bg-[#F8F9FA]">
                <Header />
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                    <Button
                        variant="ghost"
                        onClick={handleBack}
                        className="mb-6"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Kembali
                    </Button>
                    <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Store className="w-12 h-12 text-gray-400" />
                        </div>
                        <h3 className="font-medium text-gray-900 mb-1">{error || 'Toko tidak ditemukan'}</h3>
                        <p className="text-sm text-gray-500 mb-4">Silakan kembali ke halaman utama</p>
                        <Button onClick={handleBack} className="bg-[#0052CC] hover:bg-[#0043A8] text-white">
                            Kembali ke Beranda
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8F9FA]">
            <Header />

            <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                {/* Back Button */}
                <Button
                    variant="ghost"
                    onClick={handleBack}
                    className="mb-6 hover:bg-gray-100"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Kembali ke Beranda
                </Button>

                {/* Merchant Header */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8 mb-8">
                    <div className="flex flex-col md:flex-row gap-6">
                        {/* Avatar */}
                        <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl overflow-hidden bg-gray-100 flex-shrink-0">
                            {merchant.store_avatar ? (
                                <img
                                    src={merchant.store_avatar}
                                    alt={merchant.store_name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center bg-[#0052CC]/10">
                                    <Store className="w-12 h-12 md:w-16 md:h-16 text-[#0052CC]" />
                                </div>
                            )}
                        </div>

                        {/* Info */}
                        <div className="flex-1">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                                <div>
                                    <h1 className="font-heading font-bold text-2xl md:text-3xl text-gray-900 mb-2">
                                        {merchant.store_name}
                                    </h1>
                                    <p className="text-gray-600 mb-3">{merchant.address}</p>
                                </div>

                                <span
                                    className={`
                    self-start px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2
                    ${merchant.is_open === 'open'
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-red-100 text-red-700'
                                        }
                  `}
                                >
                                    <Clock className="w-4 h-4" />
                                    {merchant.is_open === 'open' ? 'Buka' : 'Tutup'}
                                </span>
                            </div>

                            {/* Meta */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="flex items-center gap-2 text-gray-600">
                                    <MapPin className="w-5 h-5 text-[#0052CC]" />
                                    <div>
                                        <p className="text-sm font-medium">{merchant.distance}</p>
                                        <p className="text-xs">{merchant.district}, {merchant.city}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 text-gray-600">
                                    <Package className="w-5 h-5 text-[#0052CC]" />
                                    <div>
                                        <p className="text-sm font-medium">{merchant.total_product} Produk</p>
                                        <p className="text-xs">Tersedia</p>
                                    </div>
                                </div>

                                {merchant.phone && (
                                    <div className="flex items-center gap-2 text-gray-600">
                                        <Phone className="w-5 h-5 text-[#0052CC]" />
                                        <div>
                                            <p className="text-sm font-medium">{merchant.phone}</p>
                                            <p className="text-xs">Kontak</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Products Section */}
                <div className="mb-6">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <Package className="w-6 h-6 text-[#0052CC]" />
                        Produk dari {merchant.store_name}
                    </h2>

                    {merchant.products && merchant.products.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {merchant.products.map((product) => (
                                <ProductCard
                                    key={product.product_id}
                                    product={product}
                                    merchant={merchant}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Package className="w-12 h-12 text-gray-400" />
                            </div>
                            <h3 className="font-medium text-gray-900 mb-1">Tidak ada produk</h3>
                            <p className="text-sm text-gray-500">Toko ini belum memiliki produk</p>
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
}
